import { assertFinanceTemplateWriteAccess, assertInvoiceDeductionItemWriteAccess, assertInvoiceGroupWriteAccess, assertInvoiceLineItemWriteAccess, assertInvoiceWriteAccess, assertProjectWriteAccess, assertQuoteDeductionItemWriteAccess, assertQuoteGroupWriteAccess, assertQuoteLineItemWriteAccess, assertQuoteVersionWriteAccess, replaceQuoteGroupTemplateItems, toZeroDate, } from "../projects-helpers.js";
function authorizedQuoteLineItemTemplatesQuery(ctx, projectZql) {
    if (ctx.role === "admin" || ctx.role === "employee") {
        return projectZql.quoteLineItemTemplates;
    }
    return projectZql.quoteLineItemTemplates.where("id", "__unauthorized__");
}
function authorizedQuoteGroupTemplatesQuery(ctx, projectZql) {
    if (ctx.role === "admin" || ctx.role === "employee") {
        return projectZql.quoteGroupTemplates;
    }
    return projectZql.quoteGroupTemplates.where("id", "__unauthorized__");
}
function quoteGroupTemplatesWithItems(ctx, projectZql) {
    return authorizedQuoteGroupTemplatesQuery(ctx, projectZql).related("items", (item) => item.related("template"));
}
function authorizedQuotesQuery(ctx, projectZql) {
    if (ctx.role === "admin" || ctx.role === "employee") {
        return projectZql.quotes;
    }
    const personID = ctx.personID;
    return personID
        ? projectZql.quotes.whereExists("project", (project) => project.whereExists("collaborators", (collaborator) => collaborator.where("personID", personID)))
        : projectZql.quotes.where("id", "__unauthorized__");
}
function quotesWithRelations(ctx, projectZql) {
    return authorizedQuotesQuery(ctx, projectZql)
        .related("versions")
        .related("customer")
        .related("contact");
}
function invoicesWithRelations(ctx, projectZql) {
    return authorizedInvoicesQuery(ctx, projectZql)
        .related("project", (project) => project
        .related("companies")
        .related("contact")
        .related("account_manager"))
        .related("customer")
        .related("contact");
}
function authorizedQuoteGroupsQuery(ctx, projectZql) {
    if (ctx.role === "admin" || ctx.role === "employee") {
        return projectZql.quoteGroups;
    }
    const personID = ctx.personID;
    return personID
        ? projectZql.quoteGroups.whereExists("version", (version) => version.whereExists("quote", (quote) => quote.whereExists("project", (project) => project.whereExists("collaborators", (collaborator) => collaborator.where("personID", personID)))))
        : projectZql.quoteGroups.where("id", "__unauthorized__");
}
function authorizedQuoteDeductionItemsQuery(ctx, projectZql) {
    if (ctx.role === "admin" || ctx.role === "employee") {
        return projectZql.quoteDeductionItems;
    }
    const personID = ctx.personID;
    return personID
        ? projectZql.quoteDeductionItems.whereExists("version", (version) => version.whereExists("quote", (quote) => quote.whereExists("project", (project) => project.whereExists("collaborators", (collaborator) => collaborator.where("personID", personID)))))
        : projectZql.quoteDeductionItems.where("id", "__unauthorized__");
}
function authorizedInvoicesQuery(ctx, projectZql) {
    if (ctx.role === "admin" || ctx.role === "employee") {
        return projectZql.invoices;
    }
    const personID = ctx.personID;
    return personID
        ? projectZql.invoices.whereExists("project", (project) => project.whereExists("collaborators", (collaborator) => collaborator.where("personID", personID)))
        : projectZql.invoices.where("id", "__unauthorized__");
}
function authorizedInvoiceGroupsQuery(ctx, projectZql) {
    if (ctx.role === "admin" || ctx.role === "employee") {
        return projectZql.invoiceGroups;
    }
    const personID = ctx.personID;
    return personID
        ? projectZql.invoiceGroups.whereExists("invoice", (invoice) => invoice.whereExists("project", (project) => project.whereExists("collaborators", (collaborator) => collaborator.where("personID", personID))))
        : projectZql.invoiceGroups.where("id", "__unauthorized__");
}
function authorizedInvoiceRemindersQuery(ctx, projectZql) {
    if (ctx.role === "admin" || ctx.role === "employee") {
        return projectZql.invoiceReminders;
    }
    return projectZql.invoiceReminders.where("id", "__unauthorized__");
}
function assertInvoiceReminderRole(ctx) {
    if (ctx.role !== "admin" && ctx.role !== "employee") {
        throw new Error("Not authorized to manage invoice reminders");
    }
}
function authorizedInvoiceDeductionItemsQuery(ctx, projectZql) {
    if (ctx.role === "admin" || ctx.role === "employee") {
        return projectZql.invoiceDeductionItems;
    }
    const personID = ctx.personID;
    return personID
        ? projectZql.invoiceDeductionItems.whereExists("invoice", (invoice) => invoice.whereExists("project", (project) => project.whereExists("collaborators", (collaborator) => collaborator.where("personID", personID))))
        : projectZql.invoiceDeductionItems.where("id", "__unauthorized__");
}
export function createFinanceQueries({ defineProjectQuery, projectZql, }) {
    return {
        quoteLineItemTemplates: {
            list: defineProjectQuery(({ ctx }) => authorizedQuoteLineItemTemplatesQuery(ctx, projectZql).orderBy("sortOrder", "asc")),
        },
        quoteGroupTemplates: {
            list: defineProjectQuery(({ ctx }) => quoteGroupTemplatesWithItems(ctx, projectZql)
                .orderBy("sortOrder", "asc")
                .orderBy("createdAt", "asc")),
        },
        quotes: {
            byProject: defineProjectQuery(({ args, ctx }) => quotesWithRelations(ctx, projectZql)
                .where("projectID", args.projectID)
                .orderBy("createdAt", "desc")),
            byID: defineProjectQuery(({ args, ctx }) => quotesWithRelations(ctx, projectZql).where("id", args.id).one()),
        },
        quoteGroups: {
            byVersion: defineProjectQuery(({ args, ctx, }) => authorizedQuoteGroupsQuery(ctx, projectZql)
                .where("quoteVersionID", args.quoteVersionID)
                .related("items")
                .orderBy("displayOrder", "asc")),
        },
        quoteDeductionItems: {
            byVersion: defineProjectQuery(({ args, ctx, }) => authorizedQuoteDeductionItemsQuery(ctx, projectZql)
                .where("quoteVersionID", args.quoteVersionID)
                .orderBy("displayOrder", "asc")),
        },
        invoices: {
            list: defineProjectQuery(({ ctx }) => invoicesWithRelations(ctx, projectZql)
                .where("deletedAt", "IS", null)
                .orderBy("createdAt", "desc")),
            byProject: defineProjectQuery(({ args, ctx }) => invoicesWithRelations(ctx, projectZql)
                .where("projectID", args.projectID)
                .where("deletedAt", "IS", null)
                .orderBy("createdAt", "desc")),
            byID: defineProjectQuery(({ args, ctx }) => invoicesWithRelations(ctx, projectZql).where("id", args.id).one()),
            byQuoteVersion: defineProjectQuery(({ args, ctx, }) => authorizedInvoicesQuery(ctx, projectZql)
                .where("sourceQuoteVersionID", args.quoteVersionID)
                .where("deletedAt", "IS", null)
                .where((eb) => eb.cmp("status", "!=", "cancelled"))
                .orderBy("createdAt", "asc")),
        },
        invoiceGroups: {
            byInvoice: defineProjectQuery(({ args, ctx }) => authorizedInvoiceGroupsQuery(ctx, projectZql)
                .where("invoiceID", args.invoiceID)
                .related("items")
                .orderBy("displayOrder", "asc")),
        },
        invoiceReminders: {
            byInvoice: defineProjectQuery(({ args, ctx }) => authorizedInvoiceRemindersQuery(ctx, projectZql)
                .where("invoiceID", args.invoiceID)
                .orderBy("reminderNumber", "asc")),
            byID: defineProjectQuery(({ args, ctx }) => authorizedInvoiceRemindersQuery(ctx, projectZql)
                .where("id", args.id)
                .related("invoice")
                .one()),
        },
        invoiceDeductionItems: {
            byInvoice: defineProjectQuery(({ args, ctx }) => authorizedInvoiceDeductionItemsQuery(ctx, projectZql)
                .where("invoiceID", args.invoiceID)
                .orderBy("displayOrder", "asc")),
        },
    };
}
export function createFinanceMutators({ defineProjectMutator, projectZql, schemas, }) {
    const { createQuoteLineItemTemplateInputSchema, updateQuoteLineItemTemplateInputSchema, quoteLineItemTemplateIDInputSchema, renameQuoteLineItemTemplateCategoryInputSchema, createQuoteGroupTemplateInputSchema, updateQuoteGroupTemplateInputSchema, quoteGroupTemplateIDInputSchema, renameQuoteGroupTemplateCategoryInputSchema, createQuoteInputSchema, updateQuoteInputSchema, quoteIDInputSchema, createQuoteVersionInputSchema, updateQuoteVersionInputSchema, quoteVersionIDInputSchema, setQuoteVersionStatusInputSchema, createInvoiceInputSchema, updateInvoiceInputSchema, invoiceIDInputSchema, transitionInvoiceStatusInputSchema, createInvoiceReminderInputSchema, updateInvoiceReminderInputSchema, invoiceReminderIDInputSchema, createInvoiceGroupInputSchema, updateInvoiceGroupInputSchema, invoiceGroupIDInputSchema, duplicateInvoiceGroupInputSchema, copyQuoteContentToInvoiceInputSchema, createInvoiceLineItemInputSchema, updateInvoiceLineItemInputSchema, invoiceLineItemIDInputSchema, reorderInvoiceLineItemsInputSchema, createInvoiceDeductionItemInputSchema, updateInvoiceDeductionItemInputSchema, invoiceDeductionItemIDInputSchema, createQuoteGroupInputSchema, updateQuoteGroupInputSchema, quoteGroupIDInputSchema, duplicateQuoteGroupInputSchema, duplicateQuoteVersionContentInputSchema, createQuoteLineItemInputSchema, updateQuoteLineItemInputSchema, quoteLineItemIDInputSchema, reorderQuoteLineItemsInputSchema, createQuoteDeductionItemInputSchema, updateQuoteDeductionItemInputSchema, quoteDeductionItemIDInputSchema, } = schemas;
    return {
        quoteLineItemTemplates: {
            create: defineProjectMutator(createQuoteLineItemTemplateInputSchema, async ({ tx, ctx, args, }) => {
                assertFinanceTemplateWriteAccess(ctx);
                const now = Date.now();
                await tx.mutate.quoteLineItemTemplates.insert({
                    id: args.id,
                    name: args.name,
                    code: args.code ?? null,
                    defaultAmount: args.defaultAmount ?? 1,
                    defaultUnitPrice: args.defaultUnitPrice ?? 0,
                    lineItemType: args.lineItemType ?? "generic",
                    category: args.category ?? null,
                    sortOrder: args.sortOrder ?? 0,
                    createdAt: now,
                    updatedAt: now,
                });
            }),
            update: defineProjectMutator(updateQuoteLineItemTemplateInputSchema, async ({ tx, ctx, args, }) => {
                assertFinanceTemplateWriteAccess(ctx);
                await tx.mutate.quoteLineItemTemplates.update({
                    id: args.id,
                    ...(args.name !== undefined ? { name: args.name } : {}),
                    ...(args.code !== undefined ? { code: args.code ?? null } : {}),
                    ...(args.defaultAmount !== undefined
                        ? { defaultAmount: args.defaultAmount }
                        : {}),
                    ...(args.defaultUnitPrice !== undefined
                        ? { defaultUnitPrice: args.defaultUnitPrice }
                        : {}),
                    ...(args.lineItemType !== undefined
                        ? { lineItemType: args.lineItemType }
                        : {}),
                    ...(args.category !== undefined
                        ? { category: args.category ?? null }
                        : {}),
                    ...(args.sortOrder !== undefined
                        ? { sortOrder: args.sortOrder }
                        : {}),
                    updatedAt: Date.now(),
                });
            }),
            delete: defineProjectMutator(quoteLineItemTemplateIDInputSchema, async ({ tx, ctx, args, }) => {
                assertFinanceTemplateWriteAccess(ctx);
                await tx.mutate.quoteLineItemTemplates.delete({ id: args.id });
            }),
            renameCategory: defineProjectMutator(renameQuoteLineItemTemplateCategoryInputSchema, async ({ tx, ctx, args, }) => {
                assertFinanceTemplateWriteAccess(ctx);
                const templates = await tx.run(projectZql.quoteLineItemTemplates.where("category", args.oldCategory));
                for (const template of templates ?? []) {
                    await tx.mutate.quoteLineItemTemplates.update({
                        id: template.id,
                        category: args.newCategory || null,
                        updatedAt: Date.now(),
                    });
                }
            }),
        },
        quoteGroupTemplates: {
            create: defineProjectMutator(createQuoteGroupTemplateInputSchema, async ({ tx, ctx, args, }) => {
                assertFinanceTemplateWriteAccess(ctx);
                const now = Date.now();
                await tx.mutate.quoteGroupTemplates.insert({
                    id: args.id,
                    name: args.name,
                    defaultIsOptional: args.defaultIsOptional ?? false,
                    defaultApplyProjectDiscount: args.defaultApplyProjectDiscount ?? true,
                    category: args.category ?? null,
                    sortOrder: args.sortOrder ?? 0,
                    createdAt: now,
                    updatedAt: now,
                });
                await replaceQuoteGroupTemplateItems(tx, args.id, args.items ?? []);
            }),
            update: defineProjectMutator(updateQuoteGroupTemplateInputSchema, async ({ tx, ctx, args, }) => {
                assertFinanceTemplateWriteAccess(ctx);
                const updates = {
                    id: args.id,
                    updatedAt: Date.now(),
                };
                if (args.name !== undefined)
                    updates.name = args.name;
                if (args.defaultIsOptional !== undefined) {
                    updates.defaultIsOptional = args.defaultIsOptional;
                }
                if (args.defaultApplyProjectDiscount !== undefined) {
                    updates.defaultApplyProjectDiscount =
                        args.defaultApplyProjectDiscount;
                }
                if (args.category !== undefined)
                    updates.category = args.category ?? null;
                if (args.sortOrder !== undefined)
                    updates.sortOrder = args.sortOrder;
                if (Object.keys(updates).length > 2) {
                    await tx.mutate.quoteGroupTemplates.update(updates);
                }
                if (args.items !== undefined) {
                    await replaceQuoteGroupTemplateItems(tx, args.id, args.items);
                }
            }),
            delete: defineProjectMutator(quoteGroupTemplateIDInputSchema, async ({ tx, ctx, args, }) => {
                assertFinanceTemplateWriteAccess(ctx);
                await tx.mutate.quoteGroupTemplates.delete({ id: args.id });
            }),
            renameCategory: defineProjectMutator(renameQuoteGroupTemplateCategoryInputSchema, async ({ tx, ctx, args, }) => {
                assertFinanceTemplateWriteAccess(ctx);
                const templates = await tx.run(projectZql.quoteGroupTemplates.where("category", args.oldCategory));
                for (const template of templates ?? []) {
                    await tx.mutate.quoteGroupTemplates.update({
                        id: template.id,
                        category: args.newCategory || null,
                        updatedAt: Date.now(),
                    });
                }
            }),
        },
        quotes: {
            create: defineProjectMutator(createQuoteInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                await tx.mutate.quotes.insert({
                    id: args.id,
                    projectID: args.projectID,
                    name: args.name,
                });
            }),
            update: defineProjectMutator(updateQuoteInputSchema, async ({ tx, ctx, args, }) => {
                const quote = await tx.run(projectZql.quotes.where("id", args.id).one());
                if (!quote)
                    throw new Error("Quote not found");
                await assertProjectWriteAccess(tx, ctx, quote.projectID);
                await tx.mutate.quotes.update({
                    id: args.id,
                    ...(args.name !== undefined ? { name: args.name } : {}),
                    ...(args.customerID !== undefined
                        ? { customerID: args.customerID ?? null }
                        : {}),
                    ...(args.contactID !== undefined
                        ? { contactID: args.contactID ?? null }
                        : {}),
                });
            }),
            delete: defineProjectMutator(quoteIDInputSchema, async ({ tx, ctx, args, }) => {
                const quote = await tx.run(projectZql.quotes.where("id", args.id).one());
                if (!quote)
                    throw new Error("Quote not found");
                await assertProjectWriteAccess(tx, ctx, quote.projectID);
                await tx.mutate.quotes.delete({ id: args.id });
            }),
        },
        quoteVersions: {
            create: defineProjectMutator(createQuoteVersionInputSchema, async ({ tx, ctx, args, }) => {
                const quote = await tx.run(projectZql.quotes.where("id", args.quoteID).one());
                if (!quote)
                    throw new Error("Quote not found");
                await assertProjectWriteAccess(tx, ctx, quote.projectID);
                await tx.mutate.quoteVersions.insert({
                    id: args.id,
                    quoteID: args.quoteID,
                    versionNumber: args.versionNumber,
                    versionLabel: String(args.versionNumber),
                    internalName: args.internalName ?? null,
                    ...(args.quoteDate
                        ? { quoteDate: toZeroDate(args.quoteDate) }
                        : {}),
                    validityDays: args.validityDays ?? 20,
                    isAccepted: false,
                    projectDiscountPercent: args.projectDiscountPercent ?? 0,
                    vatRate: args.vatRate ?? "CH_8_1",
                    content: { groups: [] },
                    status: "draft",
                    generalConditions: args.generalConditions ?? null,
                });
            }),
            update: defineProjectMutator(updateQuoteVersionInputSchema, async ({ tx, ctx, args, }) => {
                const version = await tx.run(projectZql.quoteVersions.where("id", args.id).one());
                if (!version)
                    throw new Error("Quote version not found");
                const quote = await tx.run(projectZql.quotes.where("id", version.quoteID).one());
                if (!quote)
                    throw new Error("Quote not found");
                await assertProjectWriteAccess(tx, ctx, quote.projectID);
                await tx.mutate.quoteVersions.update({
                    id: args.id,
                    ...(args.internalName !== undefined
                        ? { internalName: args.internalName ?? null }
                        : {}),
                    ...(args.quoteDate !== undefined
                        ? { quoteDate: toZeroDate(args.quoteDate) }
                        : {}),
                    ...(args.validUntil !== undefined
                        ? { validUntil: toZeroDate(args.validUntil) }
                        : {}),
                    ...(args.validityDays !== undefined
                        ? { validityDays: args.validityDays }
                        : {}),
                    ...(args.isAccepted !== undefined
                        ? { isAccepted: args.isAccepted }
                        : {}),
                    ...(args.pdfURL !== undefined
                        ? { pdfURL: args.pdfURL ?? null }
                        : {}),
                    ...(args.projectDiscountPercent !== undefined
                        ? { projectDiscountPercent: args.projectDiscountPercent }
                        : {}),
                    ...(args.vatRate !== undefined ? { vatRate: args.vatRate } : {}),
                    ...(args.generalConditions !== undefined
                        ? { generalConditions: args.generalConditions ?? null }
                        : {}),
                    ...(args.status !== undefined ? { status: args.status } : {}),
                });
            }),
            delete: defineProjectMutator(quoteVersionIDInputSchema, async ({ tx, ctx, args, }) => {
                const version = await tx.run(projectZql.quoteVersions.where("id", args.id).one());
                if (!version)
                    throw new Error("Quote version not found");
                const quote = await tx.run(projectZql.quotes.where("id", version.quoteID).one());
                if (!quote)
                    throw new Error("Quote not found");
                await assertProjectWriteAccess(tx, ctx, quote.projectID);
                await tx.mutate.quoteVersions.update({
                    id: args.id,
                    deletedAt: Date.now(),
                });
            }),
            setStatus: defineProjectMutator(setQuoteVersionStatusInputSchema, async ({ tx, ctx, args, }) => {
                const version = await tx.run(projectZql.quoteVersions.where("id", args.id).one());
                if (!version)
                    throw new Error("Quote version not found");
                const quote = await tx.run(projectZql.quotes.where("id", version.quoteID).one());
                if (!quote)
                    throw new Error("Quote not found");
                await assertProjectWriteAccess(tx, ctx, quote.projectID);
                await tx.mutate.quoteVersions.update({
                    id: args.id,
                    status: args.status,
                    isAccepted: args.status === "accepted",
                });
            }),
        },
        invoices: {
            create: defineProjectMutator(createInvoiceInputSchema, async ({ tx, ctx, args, }) => {
                if (args.projectID) {
                    await assertProjectWriteAccess(tx, ctx, args.projectID);
                }
                else if (ctx.role !== "admin" && ctx.role !== "employee") {
                    throw new Error("Not allowed to create standalone invoices");
                }
                await tx.mutate.invoices.insert({
                    id: args.id,
                    projectID: args.projectID ?? null,
                    title: args.title,
                    language: args.language ?? null,
                    currency: args.currency ?? "CHF",
                    sourceQuoteVersionID: args.sourceQuoteVersionID ?? null,
                    customerID: args.customerID ?? null,
                    contactID: args.contactID ?? null,
                    status: "draft",
                    paymentTermDays: 30,
                    projectDiscountPercent: args.projectDiscountPercent ?? 0,
                    vatRate: args.vatRate ?? "CH_8_1",
                    depositPercent: args.depositPercent ?? null,
                    includeQRBill: (args.currency ?? "CHF") === "CHF" &&
                        (args.includeQRBill ?? true),
                    totalNet: 0,
                    totalGross: 0,
                    createdBy: ctx.userID ?? null,
                });
            }),
            update: defineProjectMutator(updateInvoiceInputSchema, async ({ tx, ctx, args, }) => {
                const invoice = await assertInvoiceWriteAccess(tx, ctx, args.id);
                await tx.mutate.invoices.update({
                    id: args.id,
                    ...(args.title !== undefined ? { title: args.title } : {}),
                    ...(args.language !== undefined
                        ? { language: args.language ?? null }
                        : {}),
                    ...(args.currency !== undefined
                        ? {
                            currency: args.currency,
                            ...(args.currency === "EUR" ? { includeQRBill: false } : {}),
                        }
                        : {}),
                    ...(args.invoiceDate !== undefined
                        ? { invoiceDate: toZeroDate(args.invoiceDate) }
                        : {}),
                    ...(args.dueDate !== undefined
                        ? { dueDate: toZeroDate(args.dueDate) }
                        : {}),
                    ...(args.paymentTermDays !== undefined
                        ? { paymentTermDays: args.paymentTermDays }
                        : {}),
                    ...(args.projectDiscountPercent !== undefined
                        ? { projectDiscountPercent: args.projectDiscountPercent }
                        : {}),
                    ...(args.vatRate !== undefined ? { vatRate: args.vatRate } : {}),
                    ...(args.depositPercent !== undefined
                        ? { depositPercent: args.depositPercent }
                        : {}),
                    ...(args.includeQRBill !== undefined
                        ? {
                            includeQRBill: (args.currency ?? invoice.currency) === "EUR"
                                ? false
                                : args.includeQRBill,
                        }
                        : {}),
                    ...(args.pdfURL !== undefined
                        ? { pdfURL: args.pdfURL ?? null }
                        : {}),
                    ...(args.notes !== undefined ? { notes: args.notes ?? null } : {}),
                    ...(args.totalNet !== undefined ? { totalNet: args.totalNet } : {}),
                    ...(args.totalGross !== undefined
                        ? { totalGross: args.totalGross }
                        : {}),
                    ...(args.qrReference !== undefined
                        ? { qrReference: args.qrReference ?? null }
                        : {}),
                    ...(args.customerID !== undefined
                        ? { customerID: args.customerID ?? null }
                        : {}),
                    ...(args.contactID !== undefined
                        ? { contactID: args.contactID ?? null }
                        : {}),
                    ...(args.status !== undefined ? { status: args.status } : {}),
                });
            }),
            delete: defineProjectMutator(invoiceIDInputSchema, async ({ tx, ctx, args, }) => {
                await assertInvoiceWriteAccess(tx, ctx, args.id);
                await tx.mutate.invoices.update({
                    id: args.id,
                    deletedAt: Date.now(),
                });
            }),
            transitionStatus: defineProjectMutator(transitionInvoiceStatusInputSchema, async ({ tx, ctx, args, }) => {
                const invoice = await assertInvoiceWriteAccess(tx, ctx, args.id);
                const validTransitions = {
                    draft: ["sent", "cancelled"],
                    sent: ["draft", "paid", "cancelled"],
                    paid: ["draft", "sent", "cancelled"],
                    cancelled: ["draft"],
                };
                const currentStatus = invoice.status;
                if (!validTransitions[currentStatus]?.includes(args.status)) {
                    throw new Error(`Cannot transition from ${currentStatus} to ${args.status}`);
                }
                if (args.status === "paid" && !invoice.pdfURL) {
                    throw new Error("Cannot mark invoice as paid before generating PDF");
                }
                const updates = {
                    id: args.id,
                    status: args.status,
                };
                if (args.status === "sent" && !invoice.invoiceDate) {
                    const today = new Date();
                    const todayText = today.toISOString().split("T")[0];
                    updates.invoiceDate = toZeroDate(todayText);
                    if (!invoice.dueDate) {
                        today.setDate(today.getDate() + invoice.paymentTermDays);
                        updates.dueDate = toZeroDate(today.toISOString().split("T")[0]);
                    }
                }
                await tx.mutate.invoices.update(updates);
            }),
        },
        invoiceReminders: {
            create: defineProjectMutator(createInvoiceReminderInputSchema, async ({ tx, ctx, args, }) => {
                assertInvoiceReminderRole(ctx);
                await assertInvoiceWriteAccess(tx, ctx, args.invoiceID);
                const existing = await tx.run(projectZql.invoiceReminders
                    .where("invoiceID", args.invoiceID)
                    .orderBy("reminderNumber", "desc"));
                const nextReminderNumber = Math.max(0, ...(existing ?? []).map((reminder) => reminder.reminderNumber ?? 0)) + 1;
                await tx.mutate.invoiceReminders.insert({
                    id: args.id,
                    invoiceID: args.invoiceID,
                    reminderNumber: nextReminderNumber,
                    title: args.title,
                    reminderDate: toZeroDate(args.reminderDate),
                    dueDate: toZeroDate(args.dueDate),
                    paymentTermDays: args.paymentTermDays,
                    pdfURL: args.pdfURL ?? null,
                    status: "sent",
                    totalNet: args.totalNet,
                    totalGross: args.totalGross,
                    createdBy: ctx.userID ?? null,
                });
            }),
            update: defineProjectMutator(updateInvoiceReminderInputSchema, async ({ tx, ctx, args, }) => {
                assertInvoiceReminderRole(ctx);
                const reminder = await tx.run(projectZql.invoiceReminders.where("id", args.id).one());
                if (!reminder)
                    throw new Error("Invoice reminder not found");
                await assertInvoiceWriteAccess(tx, ctx, reminder.invoiceID);
                await tx.mutate.invoiceReminders.update({
                    id: args.id,
                    ...(args.title !== undefined ? { title: args.title } : {}),
                    ...(args.reminderDate !== undefined
                        ? { reminderDate: toZeroDate(args.reminderDate) }
                        : {}),
                    ...(args.dueDate !== undefined
                        ? { dueDate: toZeroDate(args.dueDate) }
                        : {}),
                    ...(args.paymentTermDays !== undefined
                        ? { paymentTermDays: args.paymentTermDays }
                        : {}),
                    ...(args.pdfURL !== undefined
                        ? { pdfURL: args.pdfURL ?? null }
                        : {}),
                    ...(args.status !== undefined ? { status: args.status } : {}),
                    ...(args.totalNet !== undefined ? { totalNet: args.totalNet } : {}),
                    ...(args.totalGross !== undefined
                        ? { totalGross: args.totalGross }
                        : {}),
                });
            }),
            delete: defineProjectMutator(invoiceReminderIDInputSchema, async ({ tx, ctx, args, }) => {
                assertInvoiceReminderRole(ctx);
                const reminder = await tx.run(projectZql.invoiceReminders.where("id", args.id).one());
                if (!reminder)
                    throw new Error("Invoice reminder not found");
                await assertInvoiceWriteAccess(tx, ctx, reminder.invoiceID);
                await tx.mutate.invoiceReminders.delete({ id: args.id });
            }),
        },
        invoiceGroups: {
            create: defineProjectMutator(createInvoiceGroupInputSchema, async ({ tx, ctx, args, }) => {
                await assertInvoiceWriteAccess(tx, ctx, args.invoiceID);
                await tx.mutate.invoiceGroups.insert({
                    id: args.id,
                    invoiceID: args.invoiceID,
                    name: args.name ?? "New Group",
                    displayOrder: args.displayOrder ?? 0,
                    isOptional: args.isOptional ?? false,
                    applyProjectDiscount: args.applyProjectDiscount ?? true,
                });
            }),
            update: defineProjectMutator(updateInvoiceGroupInputSchema, async ({ tx, ctx, args, }) => {
                await assertInvoiceGroupWriteAccess(tx, ctx, args.id);
                await tx.mutate.invoiceGroups.update({
                    id: args.id,
                    ...(args.name !== undefined ? { name: args.name } : {}),
                    ...(args.displayOrder !== undefined
                        ? { displayOrder: args.displayOrder }
                        : {}),
                    ...(args.isOptional !== undefined
                        ? { isOptional: args.isOptional }
                        : {}),
                    ...(args.applyProjectDiscount !== undefined
                        ? { applyProjectDiscount: args.applyProjectDiscount }
                        : {}),
                });
            }),
            delete: defineProjectMutator(invoiceGroupIDInputSchema, async ({ tx, ctx, args, }) => {
                await assertInvoiceGroupWriteAccess(tx, ctx, args.id);
                await tx.mutate.invoiceGroups.delete({ id: args.id });
            }),
            duplicate: defineProjectMutator(duplicateInvoiceGroupInputSchema, async ({ tx, ctx, args, }) => {
                const sourceGroup = await assertInvoiceGroupWriteAccess(tx, ctx, args.id);
                const sourceItems = await tx.run(projectZql.invoiceLineItems
                    .where("invoiceGroupID", args.id)
                    .orderBy("displayOrder", "asc"));
                const itemIDBySourceID = new Map(args.itemIDs.map((item) => [item.sourceID, item.newID]));
                await tx.mutate.invoiceGroups.insert({
                    id: args.newID,
                    invoiceID: sourceGroup.invoiceID,
                    name: `${sourceGroup.name} (Copy)`,
                    displayOrder: sourceGroup.displayOrder + 1,
                    isOptional: sourceGroup.isOptional,
                    applyProjectDiscount: sourceGroup.applyProjectDiscount,
                });
                for (const item of sourceItems) {
                    const id = itemIDBySourceID.get(item.id);
                    if (!id)
                        throw new Error("Missing duplicate line item id");
                    await tx.mutate.invoiceLineItems.insert({
                        id,
                        invoiceGroupID: args.newID,
                        name: item.name,
                        amount: item.amount,
                        factor: item.factor,
                        unitPrice: item.unitPrice,
                        discount: item.discount,
                        notes: item.notes,
                        externalNote: item.externalNote,
                        displayOrder: item.displayOrder,
                        isTitle: item.isTitle,
                        lineItemType: item.lineItemType,
                    });
                }
            }),
            copyQuoteContent: defineProjectMutator(copyQuoteContentToInvoiceInputSchema, async ({ tx, ctx, args, }) => {
                await assertQuoteVersionWriteAccess(tx, ctx, args.sourceQuoteVersionID);
                await assertInvoiceWriteAccess(tx, ctx, args.targetInvoiceID);
                const sourceGroups = await tx.run(projectZql.quoteGroups
                    .where("quoteVersionID", args.sourceQuoteVersionID)
                    .orderBy("displayOrder", "asc"));
                for (const group of sourceGroups) {
                    const newGroupID = crypto.randomUUID();
                    await tx.mutate.invoiceGroups.insert({
                        id: newGroupID,
                        invoiceID: args.targetInvoiceID,
                        name: group.name,
                        displayOrder: group.displayOrder,
                        isOptional: group.isOptional,
                        applyProjectDiscount: group.applyProjectDiscount,
                    });
                    const sourceItems = await tx.run(projectZql.quoteLineItems
                        .where("quoteGroupID", group.id)
                        .orderBy("displayOrder", "asc"));
                    for (const item of sourceItems) {
                        await tx.mutate.invoiceLineItems.insert({
                            id: crypto.randomUUID(),
                            invoiceGroupID: newGroupID,
                            name: item.name,
                            amount: item.amount,
                            factor: item.factor,
                            unitPrice: item.unitPrice,
                            discount: item.discount,
                            notes: item.notes,
                            externalNote: item.externalNote,
                            displayOrder: item.displayOrder,
                            isTitle: item.isTitle,
                            lineItemType: item.lineItemType,
                        });
                    }
                }
                const sourceDeductions = await tx.run(projectZql.quoteDeductionItems
                    .where("quoteVersionID", args.sourceQuoteVersionID)
                    .orderBy("displayOrder", "asc"));
                for (const deduction of sourceDeductions) {
                    await tx.mutate.invoiceDeductionItems.insert({
                        id: crypto.randomUUID(),
                        invoiceID: args.targetInvoiceID,
                        description: deduction.description,
                        amount: deduction.amount,
                        displayOrder: deduction.displayOrder,
                        sourceInvoiceID: null,
                    });
                }
            }),
        },
        invoiceLineItems: {
            create: defineProjectMutator(createInvoiceLineItemInputSchema, async ({ tx, ctx, args, }) => {
                await assertInvoiceGroupWriteAccess(tx, ctx, args.invoiceGroupID);
                await tx.mutate.invoiceLineItems.insert({
                    id: args.id,
                    invoiceGroupID: args.invoiceGroupID,
                    name: args.name ?? "",
                    amount: args.amount ?? 1,
                    factor: args.factor ?? 1,
                    unitPrice: args.unitPrice ?? 0,
                    discount: args.discount ?? 0,
                    notes: args.notes ?? "",
                    externalNote: args.externalNote ?? "",
                    displayOrder: args.displayOrder ?? 0,
                    isTitle: args.isTitle ?? false,
                    lineItemType: args.lineItemType ?? "generic",
                });
            }),
            update: defineProjectMutator(updateInvoiceLineItemInputSchema, async ({ tx, ctx, args, }) => {
                const existing = await assertInvoiceLineItemWriteAccess(tx, ctx, args.id);
                if (args.invoiceGroupID !== undefined &&
                    args.invoiceGroupID !== existing.invoiceGroupID) {
                    await assertInvoiceGroupWriteAccess(tx, ctx, args.invoiceGroupID);
                }
                await tx.mutate.invoiceLineItems.update({
                    id: args.id,
                    ...(args.invoiceGroupID !== undefined
                        ? { invoiceGroupID: args.invoiceGroupID }
                        : {}),
                    ...(args.name !== undefined ? { name: args.name } : {}),
                    ...(args.amount !== undefined ? { amount: args.amount } : {}),
                    ...(args.factor !== undefined ? { factor: args.factor } : {}),
                    ...(args.unitPrice !== undefined
                        ? { unitPrice: args.unitPrice }
                        : {}),
                    ...(args.discount !== undefined ? { discount: args.discount } : {}),
                    ...(args.notes !== undefined ? { notes: args.notes } : {}),
                    ...(args.externalNote !== undefined
                        ? { externalNote: args.externalNote }
                        : {}),
                    ...(args.displayOrder !== undefined
                        ? { displayOrder: args.displayOrder }
                        : {}),
                    ...(args.isTitle !== undefined ? { isTitle: args.isTitle } : {}),
                    ...(args.lineItemType !== undefined
                        ? { lineItemType: args.lineItemType }
                        : {}),
                });
            }),
            delete: defineProjectMutator(invoiceLineItemIDInputSchema, async ({ tx, ctx, args, }) => {
                await assertInvoiceLineItemWriteAccess(tx, ctx, args.id);
                await tx.mutate.invoiceLineItems.delete({ id: args.id });
            }),
            reorder: defineProjectMutator(reorderInvoiceLineItemsInputSchema, async ({ tx, ctx, args, }) => {
                await assertInvoiceGroupWriteAccess(tx, ctx, args.invoiceGroupID);
                const rows = await tx.run(projectZql.invoiceLineItems.where("invoiceGroupID", args.invoiceGroupID));
                const rowIDs = new Set(rows.map((row) => row.id));
                if (rowIDs.size !== args.orderedIDs.length) {
                    throw new Error("Cannot reorder stale invoice line item list");
                }
                for (const id of args.orderedIDs) {
                    if (!rowIDs.has(id)) {
                        throw new Error("Cannot reorder invoice line item outside group");
                    }
                }
                for (const [displayOrder, id] of args.orderedIDs.entries()) {
                    await tx.mutate.invoiceLineItems.update({ id, displayOrder });
                }
            }),
        },
        invoiceDeductionItems: {
            create: defineProjectMutator(createInvoiceDeductionItemInputSchema, async ({ tx, ctx, args, }) => {
                await assertInvoiceWriteAccess(tx, ctx, args.invoiceID);
                if (args.sourceInvoiceID) {
                    await assertInvoiceWriteAccess(tx, ctx, args.sourceInvoiceID);
                }
                await tx.mutate.invoiceDeductionItems.insert({
                    id: args.id,
                    invoiceID: args.invoiceID,
                    description: args.description ?? "",
                    amount: args.amount ?? 0,
                    displayOrder: args.displayOrder ?? 0,
                    sourceInvoiceID: args.sourceInvoiceID ?? null,
                });
            }),
            update: defineProjectMutator(updateInvoiceDeductionItemInputSchema, async ({ tx, ctx, args, }) => {
                await assertInvoiceDeductionItemWriteAccess(tx, ctx, args.id);
                if (args.sourceInvoiceID) {
                    await assertInvoiceWriteAccess(tx, ctx, args.sourceInvoiceID);
                }
                await tx.mutate.invoiceDeductionItems.update({
                    id: args.id,
                    ...(args.description !== undefined
                        ? { description: args.description }
                        : {}),
                    ...(args.amount !== undefined ? { amount: args.amount } : {}),
                    ...(args.displayOrder !== undefined
                        ? { displayOrder: args.displayOrder }
                        : {}),
                    ...(args.sourceInvoiceID !== undefined
                        ? { sourceInvoiceID: args.sourceInvoiceID }
                        : {}),
                });
            }),
            delete: defineProjectMutator(invoiceDeductionItemIDInputSchema, async ({ tx, ctx, args, }) => {
                await assertInvoiceDeductionItemWriteAccess(tx, ctx, args.id);
                await tx.mutate.invoiceDeductionItems.delete({ id: args.id });
            }),
        },
        quoteGroups: {
            create: defineProjectMutator(createQuoteGroupInputSchema, async ({ tx, ctx, args, }) => {
                await assertQuoteVersionWriteAccess(tx, ctx, args.quoteVersionID);
                await tx.mutate.quoteGroups.insert({
                    id: args.id,
                    quoteVersionID: args.quoteVersionID,
                    name: args.name ?? "New Group",
                    displayOrder: args.displayOrder ?? 0,
                    isOptional: args.isOptional ?? false,
                    applyProjectDiscount: args.applyProjectDiscount ?? true,
                });
            }),
            update: defineProjectMutator(updateQuoteGroupInputSchema, async ({ tx, ctx, args, }) => {
                await assertQuoteGroupWriteAccess(tx, ctx, args.id);
                await tx.mutate.quoteGroups.update({
                    id: args.id,
                    ...(args.name !== undefined ? { name: args.name } : {}),
                    ...(args.displayOrder !== undefined
                        ? { displayOrder: args.displayOrder }
                        : {}),
                    ...(args.isOptional !== undefined
                        ? { isOptional: args.isOptional }
                        : {}),
                    ...(args.applyProjectDiscount !== undefined
                        ? { applyProjectDiscount: args.applyProjectDiscount }
                        : {}),
                });
            }),
            delete: defineProjectMutator(quoteGroupIDInputSchema, async ({ tx, ctx, args, }) => {
                await assertQuoteGroupWriteAccess(tx, ctx, args.id);
                await tx.mutate.quoteGroups.delete({ id: args.id });
            }),
            duplicate: defineProjectMutator(duplicateQuoteGroupInputSchema, async ({ tx, ctx, args, }) => {
                const sourceGroup = await assertQuoteGroupWriteAccess(tx, ctx, args.id);
                const sourceItems = await tx.run(projectZql.quoteLineItems
                    .where("quoteGroupID", args.id)
                    .orderBy("displayOrder", "asc"));
                const itemIDBySourceID = new Map(args.itemIDs.map((item) => [item.sourceID, item.newID]));
                await tx.mutate.quoteGroups.insert({
                    id: args.newID,
                    quoteVersionID: sourceGroup.quoteVersionID,
                    name: `${sourceGroup.name} (Copy)`,
                    displayOrder: sourceGroup.displayOrder + 1,
                    isOptional: sourceGroup.isOptional,
                    applyProjectDiscount: sourceGroup.applyProjectDiscount,
                });
                for (const item of sourceItems) {
                    const id = itemIDBySourceID.get(item.id);
                    if (!id)
                        throw new Error("Missing duplicate line item id");
                    await tx.mutate.quoteLineItems.insert({
                        id,
                        quoteGroupID: args.newID,
                        name: item.name,
                        amount: item.amount,
                        factor: item.factor,
                        unitPrice: item.unitPrice,
                        discount: item.discount,
                        notes: item.notes,
                        externalNote: item.externalNote,
                        displayOrder: item.displayOrder,
                        isTitle: item.isTitle,
                        lineItemType: item.lineItemType,
                    });
                }
            }),
            duplicateVersionContent: defineProjectMutator(duplicateQuoteVersionContentInputSchema, async ({ tx, ctx, args, }) => {
                await assertQuoteVersionWriteAccess(tx, ctx, args.sourceVersionID);
                await assertQuoteVersionWriteAccess(tx, ctx, args.targetVersionID);
                const sourceGroups = await tx.run(projectZql.quoteGroups
                    .where("quoteVersionID", args.sourceVersionID)
                    .orderBy("displayOrder", "asc"));
                for (const group of sourceGroups) {
                    const newGroupID = crypto.randomUUID();
                    await tx.mutate.quoteGroups.insert({
                        id: newGroupID,
                        quoteVersionID: args.targetVersionID,
                        name: group.name,
                        displayOrder: group.displayOrder,
                        isOptional: group.isOptional,
                        applyProjectDiscount: group.applyProjectDiscount,
                    });
                    const sourceItems = await tx.run(projectZql.quoteLineItems
                        .where("quoteGroupID", group.id)
                        .orderBy("displayOrder", "asc"));
                    for (const item of sourceItems) {
                        await tx.mutate.quoteLineItems.insert({
                            id: crypto.randomUUID(),
                            quoteGroupID: newGroupID,
                            name: item.name,
                            amount: item.amount,
                            factor: item.factor,
                            unitPrice: item.unitPrice,
                            discount: item.discount,
                            notes: item.notes,
                            externalNote: item.externalNote,
                            displayOrder: item.displayOrder,
                            isTitle: item.isTitle,
                            lineItemType: item.lineItemType,
                        });
                    }
                }
                const sourceDeductions = await tx.run(projectZql.quoteDeductionItems
                    .where("quoteVersionID", args.sourceVersionID)
                    .orderBy("displayOrder", "asc"));
                for (const deduction of sourceDeductions) {
                    await tx.mutate.quoteDeductionItems.insert({
                        id: crypto.randomUUID(),
                        quoteVersionID: args.targetVersionID,
                        description: deduction.description,
                        amount: deduction.amount,
                        displayOrder: deduction.displayOrder,
                    });
                }
            }),
        },
        quoteLineItems: {
            create: defineProjectMutator(createQuoteLineItemInputSchema, async ({ tx, ctx, args, }) => {
                await assertQuoteGroupWriteAccess(tx, ctx, args.quoteGroupID);
                await tx.mutate.quoteLineItems.insert({
                    id: args.id,
                    quoteGroupID: args.quoteGroupID,
                    name: args.name ?? "",
                    amount: args.amount ?? 1,
                    factor: args.factor ?? 1,
                    unitPrice: args.unitPrice ?? 0,
                    discount: args.discount ?? 0,
                    notes: args.notes ?? "",
                    externalNote: args.externalNote ?? "",
                    displayOrder: args.displayOrder ?? 0,
                    isTitle: args.isTitle ?? false,
                    lineItemType: args.lineItemType ?? "generic",
                });
            }),
            update: defineProjectMutator(updateQuoteLineItemInputSchema, async ({ tx, ctx, args, }) => {
                const existing = await assertQuoteLineItemWriteAccess(tx, ctx, args.id);
                if (args.quoteGroupID !== undefined &&
                    args.quoteGroupID !== existing.quoteGroupID) {
                    await assertQuoteGroupWriteAccess(tx, ctx, args.quoteGroupID);
                }
                await tx.mutate.quoteLineItems.update({
                    id: args.id,
                    ...(args.quoteGroupID !== undefined
                        ? { quoteGroupID: args.quoteGroupID }
                        : {}),
                    ...(args.name !== undefined ? { name: args.name } : {}),
                    ...(args.amount !== undefined ? { amount: args.amount } : {}),
                    ...(args.factor !== undefined ? { factor: args.factor } : {}),
                    ...(args.unitPrice !== undefined
                        ? { unitPrice: args.unitPrice }
                        : {}),
                    ...(args.discount !== undefined ? { discount: args.discount } : {}),
                    ...(args.notes !== undefined ? { notes: args.notes } : {}),
                    ...(args.externalNote !== undefined
                        ? { externalNote: args.externalNote }
                        : {}),
                    ...(args.displayOrder !== undefined
                        ? { displayOrder: args.displayOrder }
                        : {}),
                    ...(args.isTitle !== undefined ? { isTitle: args.isTitle } : {}),
                    ...(args.lineItemType !== undefined
                        ? { lineItemType: args.lineItemType }
                        : {}),
                });
            }),
            delete: defineProjectMutator(quoteLineItemIDInputSchema, async ({ tx, ctx, args, }) => {
                await assertQuoteLineItemWriteAccess(tx, ctx, args.id);
                await tx.mutate.quoteLineItems.delete({ id: args.id });
            }),
            reorder: defineProjectMutator(reorderQuoteLineItemsInputSchema, async ({ tx, ctx, args, }) => {
                await assertQuoteGroupWriteAccess(tx, ctx, args.quoteGroupID);
                const rows = await tx.run(projectZql.quoteLineItems.where("quoteGroupID", args.quoteGroupID));
                const rowIDs = new Set(rows.map((row) => row.id));
                if (rowIDs.size !== args.orderedIDs.length) {
                    throw new Error("Cannot reorder stale quote line item list");
                }
                for (const id of args.orderedIDs) {
                    if (!rowIDs.has(id)) {
                        throw new Error("Cannot reorder quote line item outside group");
                    }
                }
                for (const [displayOrder, id] of args.orderedIDs.entries()) {
                    await tx.mutate.quoteLineItems.update({ id, displayOrder });
                }
            }),
        },
        quoteDeductionItems: {
            create: defineProjectMutator(createQuoteDeductionItemInputSchema, async ({ tx, ctx, args, }) => {
                await assertQuoteVersionWriteAccess(tx, ctx, args.quoteVersionID);
                await tx.mutate.quoteDeductionItems.insert({
                    id: args.id,
                    quoteVersionID: args.quoteVersionID,
                    description: args.description ?? "",
                    amount: args.amount ?? 0,
                    displayOrder: args.displayOrder ?? 0,
                });
            }),
            update: defineProjectMutator(updateQuoteDeductionItemInputSchema, async ({ tx, ctx, args, }) => {
                await assertQuoteDeductionItemWriteAccess(tx, ctx, args.id);
                await tx.mutate.quoteDeductionItems.update({
                    id: args.id,
                    ...(args.description !== undefined
                        ? { description: args.description }
                        : {}),
                    ...(args.amount !== undefined ? { amount: args.amount } : {}),
                    ...(args.displayOrder !== undefined
                        ? { displayOrder: args.displayOrder }
                        : {}),
                });
            }),
            delete: defineProjectMutator(quoteDeductionItemIDInputSchema, async ({ tx, ctx, args, }) => {
                await assertQuoteDeductionItemWriteAccess(tx, ctx, args.id);
                await tx.mutate.quoteDeductionItems.delete({ id: args.id });
            }),
        },
    };
}
